import React, { useEffect, useState } from "react";
import {
  AppBar,
  Toolbar,
  Container,
  Typography,
  IconButton,
  Box,
  Badge,
  Tooltip,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  ListItemIcon,
  useTheme,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  SwipeableDrawer,
} from "@mui/material";
import {
  Menu as MenuIcon,
  HelpCircle,
  User,
  Lock,
  LogOut,
  Globe,
  LogIn,
  X as CloseIcon,
  ChevronRight,
  ShoppingCart,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link, useHistory, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import lngs from "../../lngs";
import CartDrawer from "../Cart/CartDrawer";
import useAccountProfile from "../../hooks/useAccountProfile";

export default function MenuAppBar() {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.resolvedLanguage;
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const theme = useTheme();
  const cartCount = useSelector((s) => s.cart.items.length);
  const { user, displayName, displayEmail, avatarLetter, avatarSrc } =
    useAccountProfile();
  const [scrolled, setScrolled] = useState(false);
  // State for various menus
  const [languageAnchorEl, setLanguageAnchorEl] = useState(null);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [profileAnchorEl, setProfileAnchorEl] = useState(null);

  // Menu states
  const profileOpen = Boolean(profileAnchorEl);
  const languageOpen = Boolean(languageAnchorEl);

  useEffect(() => {
    const rootElement = document.getElementById("root");

    const handleScroll = () => {
      const isScrolled = rootElement.scrollTop > 10;
      setScrolled(isScrolled);
    };

    rootElement.addEventListener("scroll", handleScroll);

    return () => {
      rootElement.removeEventListener("scroll", handleScroll);
    };
    // Only run once, on mount/unmount
  }, [scrolled]);

  // Handle menu openings and closings
  const handleProfileMenuOpen = (event) => {
    setProfileAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setProfileAnchorEl(null);
  };

  const handleLanguageMenuOpen = (event) => {
    setLanguageAnchorEl(event.currentTarget);
  };

  const handleLanguageMenuClose = () => {
    setLanguageAnchorEl(null);
  };

  const handleMobileDrawerOpen = () => {
    setMobileDrawerOpen(true);
  };

  const handleMobileDrawerClose = () => {
    setMobileDrawerOpen(false);
  };

  // Navigation and actions
  const openLink = (link) => {
    history.push(`${link}`);
    handleProfileMenuClose();
    handleMobileDrawerClose();
  };

  const logout = () => {
    dispatch({ type: "LOGOUT" });
    history.push(`/${currentLang}/login`);
    handleProfileMenuClose();
    handleMobileDrawerClose();
  };

  const handleLanguageChange = (languageCode, event) => {
    event.preventDefault();
    const currentPath = location.pathname;

    // Handle different path scenarios
    let newPath;
    if (currentPath === "/" || currentPath === "") {
      // Root path without language
      newPath = `/${languageCode}/`;
    } else {
      // Path with existing language code or other paths
      const regex = /^\/[a-z]{2}(?=\/|$)/; // Matches two-letter language code at the start of the path
      if (regex.test(currentPath)) {
        // Replace existing language code
        newPath = currentPath.replace(regex, `/${languageCode}`);
      } else {
        // No language code present, add it
        newPath = `/${languageCode}${currentPath}`;
      }
    }

    history.replace(newPath);
    i18n.changeLanguage(languageCode);
    handleLanguageMenuClose();
    handleMobileDrawerClose();
  };

  // Menu items
  const MenuSource = [
    {
      title: t("home"),
      path: `/${currentLang}/`,
      showInMobileMenu: true,
      showDesktopMenu: true,
      showBoth: false,
    },
    {
      title: t("prices"),
      path: `/${currentLang}/prices`,
      showInMobileMenu: true,
      showDesktopMenu: true,
      showBoth: false,
    },
    {
      title: t("clients"),
      path: `/${currentLang}/clients`,
      showInMobileMenu: true,
      showDesktopMenu: true,
      showBoth: false,
    },
    {
      title: t("contactus"),
      path: `/${currentLang}/contactus`,
      showInMobileMenu: true,
      showDesktopMenu: true,
      showBoth: false,
    },
    {
      title: t("about"),
      path: `/${currentLang}/about`,
      showInMobileMenu: true,
      showDesktopMenu: true,
      showBoth: false,
    },
    {
      title: t("download"),
      path: `/${currentLang}/download`,
      showInMobileMenu: true,
      showDesktopMenu: true,
      showBoth: false,
    },
    {
      title: t("faq"),
      path: `/${currentLang}/faq`,
      showInMobileMenu: true,
      showDesktopMenu: true,
      showBoth: false,
    },
    {
      title: t("signup"),
      path: `/${currentLang}/signup`,
      showInMobileMenu: false,
      showDesktopMenu: false,
      showBoth: false,
    },
  ];

  // Mobile drawer
  const renderMobileDrawer = (
    <SwipeableDrawer
      anchor="right"
      ModalProps={{ sx: { direction: "ltr" } }}
      open={mobileDrawerOpen}
      onClose={handleMobileDrawerClose}
      onOpen={handleMobileDrawerOpen}
      PaperProps={{
        sx: {
          width: 280,
          borderTopLeftRadius: 16,
          borderBottomLeftRadius: 16,
          boxShadow: "0 8px 40px rgba(0,0,0,0.12)",
        },
      }}
      sx={{ zIndex: 1300 }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: 2,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {t("menu")}
        </Typography>
        <IconButton
          edge="end"
          color="inherit"
          onClick={handleMobileDrawerClose}
          aria-label="close"
        >
          <CloseIcon size={20} />
        </IconButton>
      </Box>

      <List sx={{ pt: 1, pb: 2 }}>
        {MenuSource.filter((link) => link.showInMobileMenu === true).map(
          (link, index) => (
            <ListItem key={`drawer-menu-${index}`} disablePadding>
              <ListItemButton
                component={Link}
                to={link.path}
                onClick={handleMobileDrawerClose}
                sx={{
                  py: 1.5,
                  px: 3,
                  "&:hover": {
                    bgcolor: "rgba(25, 118, 210, 0.08)",
                  },
                }}
              >
                <ListItemText primary={link.title} />
                <ChevronRight size={16} />
              </ListItemButton>
            </ListItem>
          ),
        )}

        {!user && (
          <ListItem disablePadding>
            <ListItemButton
              component={Link}
              to={`/${currentLang}/login`}
              onClick={handleMobileDrawerClose}
              sx={{
                py: 1.5,
                px: 3,
                color: theme.palette.primary.main,
                fontWeight: 600,
                "&:hover": {
                  bgcolor: "rgba(25, 118, 210, 0.08)",
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: "auto", mr: 2, color: "inherit" }}>
                <LogIn size={18} />
              </ListItemIcon>
              <ListItemText
                primary={t("login")}
                primaryTypographyProps={{ fontWeight: 600 }}
              />
              <ChevronRight size={16} />
            </ListItemButton>
          </ListItem>
        )}
      </List>

      <Divider />

      <Box sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          {t("language")}
        </Typography>
        <List
          disablePadding
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            gap: 0.5,
          }}
        >
          {Object.keys(lngs).map((lng) => (
            <ListItem key={lng} disablePadding>
              <ListItemButton
                onClick={(e) => handleLanguageChange(lng, e)}
                sx={{
                  py: 1,
                  px: 2,
                  borderRadius: 1,
                  mb: 0.5,
                  "&:hover": {
                    bgcolor: "rgba(25, 118, 210, 0.08)",
                  },
                }}
              >
                <ListItemText primary={lngs[lng].nativeName} />
                {currentLang === lng && (
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      bgcolor: "primary.main",
                    }}
                  />
                )}
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>

      {user && (
        <>
          <Divider />
          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              {t("account")}
            </Typography>
            <List disablePadding>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => openLink(`/${currentLang}/profile`)}
                  sx={{
                    py: 1,
                    px: 2,
                    borderRadius: 1,
                    mb: 0.5,
                    "&:hover": {
                      bgcolor: "rgba(25, 118, 210, 0.08)",
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: "auto", mr: 2 }}>
                    <User size={18} />
                  </ListItemIcon>
                  <ListItemText primary={t("profile")} />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => openLink(`/${currentLang}/change-password`)}
                  sx={{
                    py: 1,
                    px: 2,
                    borderRadius: 1,
                    mb: 0.5,
                    "&:hover": {
                      bgcolor: "rgba(25, 118, 210, 0.08)",
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: "auto", mr: 2 }}>
                    <Lock size={18} />
                  </ListItemIcon>
                  <ListItemText primary={t("change_password")} />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={logout}
                  sx={{
                    py: 1,
                    px: 2,
                    borderRadius: 1,
                    color: "error.main",
                    "&:hover": {
                      bgcolor: "rgba(211, 47, 47, 0.08)",
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: "auto",
                      mr: 2,
                      color: "error.main",
                    }}
                  >
                    <LogOut size={18} />
                  </ListItemIcon>
                  <ListItemText primary={t("logout")} />
                </ListItemButton>
              </ListItem>
            </List>
          </Box>
        </>
      )}

      <Box sx={{ flexGrow: 1 }} />

      <Box sx={{ p: 3, borderTop: "1px solid", borderColor: "divider" }}>
        <Typography variant="caption" color="text.secondary">
          {new Date().getFullYear()} Quantum Billing
        </Typography>
      </Box>
    </SwipeableDrawer>
  );

  return (
    <Box sx={{ width: "100%", zIndex: 1100000, display: "contents" }}>
      <AppBar
        position="sticky"
        color="default"
        elevation={0}
        sx={{
          bgcolor: "#FFFFFF",
          borderBottom: "1px solid #E2E8F0",
          boxShadow: scrolled ? "0 2px 12px rgba(0,0,0,0.06)" : "none",
          transition: "box-shadow 0.3s ease",
        }}
      >
        <Container>
          <Toolbar disableGutters sx={{ height: 70 }}>
            {/* Logo */}
            <Link
              to={`/${currentLang}/`}
              style={{
                textDecoration: "none",
                display: "flex",
                alignItems: "center",
              }}
            >
              <img
                style={{ width: "100px", height: "50px", marginRight: "16px" }}
                src="/logo.svg"
                alt="quantum-invoice"
              />
            </Link>

            {/* Desktop Menu Items */}
            <Box
              sx={{ display: { xs: "none", md: "flex" }, alignItems: "center" }}
            >
              {MenuSource.filter((link) => link.showDesktopMenu === true).map(
                (link, index) => (
                  <MenuItem key={`desktop-menu-${index}`}>
                    <Link
                      to={link.path}
                      style={{
                        textDecoration: "none",
                        color: "#333",
                        fontWeight: 500,
                        padding: "6px 12px",
                        borderRadius: "4px",
                      }}
                    >
                      {link.title}
                    </Link>
                  </MenuItem>
                ),
              )}
            </Box>

            {/* Items shown in both mobile and desktop */}
            {MenuSource.filter((link) => link.showBoth === true).map(
              (link, index) => (
                <MenuItem key={`both-menu-${index}`}>
                  <Link
                    to={link.path}
                    style={{
                      textDecoration: "none",
                      color: "#333",
                      fontWeight: 500,
                      padding: "6px 12px",
                      borderRadius: "4px",
                    }}
                  >
                    {link.title}
                  </Link>
                </MenuItem>
              ),
            )}

            <Box sx={{ flexGrow: 1 }} />

            {/* Right side icons */}
            <Box sx={{ display: "flex", alignItems: "center" }}>
              {/* Login + Go To App buttons for non-logged-in users */}
              {!user && (
                <Box
                  sx={{
                    display: { xs: "none", sm: "flex" },
                    alignItems: "center",
                    gap: 1.5,
                    mr: 1,
                  }}
                >
                  <Button
                    component={Link}
                    to={`/${currentLang}/login`}
                    variant="outlined"
                    startIcon={<LogIn size={18} />}
                    sx={{
                      borderRadius: "8px",
                      textTransform: "none",
                      fontWeight: 600,
                      px: 2.5,
                      py: 0.9,
                      borderColor: "#09354b",
                      color: "#09354b",
                      "&:hover": { bgcolor: "#E3F2FD", borderColor: "#09354b" },
                    }}
                  >
                    {t("login")}
                  </Button>
                  <Button
                    component={Link}
                    to={`/${currentLang}/signup`}
                    variant="contained"
                    sx={{
                      borderRadius: "8px",
                      textTransform: "none",
                      fontWeight: 700,
                      px: 2.5,
                      py: 0.9,
                      bgcolor: "#09354b",
                      boxShadow: "none",
                      "&:hover": {
                        bgcolor: "#09354b",
                        boxShadow: "0 4px 12px rgba(21,101,192,0.3)",
                      },
                    }}
                  >
                    {t("signup")}
                  </Button>
                </Box>
              )}

              {/* Account shortcuts */}
              {user && (
                <>
                  <MenuItem key={`both-menu-my-account`}>
                    <Link
                      to={`/${currentLang}/my-account`}
                      style={{
                        textDecoration: "none",
                        color: "#333",
                        fontWeight: 500,
                        padding: "6px 12px",
                        borderRadius: "4px",
                      }}
                    >
                      {t("my_account")}
                    </Link>
                  </MenuItem>

                  <Tooltip title={t("help")}>
                    <IconButton
                      color="inherit"
                      size="medium"
                      sx={{ ml: 1 }}
                      onClick={() => openLink(`/${currentLang}/help`)}
                    >
                      <HelpCircle size={20} />
                    </IconButton>
                  </Tooltip>

                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{ mx: 1.5, my: 1 }}
                  />
                </>
              )}

              {/* Cart icon */}
              <Tooltip title={t("shopping_cart", { defaultValue: "Shopping cart" })}>
                <IconButton
                  aria-label={t("shopping_cart", { defaultValue: "Shopping cart" })}
                  color="inherit"
                  size="medium"
                  onClick={() => dispatch({ type: "CART_OPEN" })}
                  sx={{ ml: 0.5 }}
                >
                  <Badge badgeContent={cartCount} color="error" max={9}>
                    <ShoppingCart size={20} />
                  </Badge>
                </IconButton>
              </Tooltip>

              {/* Language selector */}
              <Tooltip title={t("language")}>
                <IconButton
                  aria-controls="language-menu"
                  aria-haspopup="true"
                  onClick={handleLanguageMenuOpen}
                  color="inherit"
                  size="medium"
                >
                  <Globe size={20} />
                </IconButton>
              </Tooltip>

              {/* Mobile menu button */}
              <Box
                sx={{
                  display: { xs: "flex", md: "none" },
                  alignItems: "center",
                }}
              >
                {/* Login Button for mobile */}
                {!user && (
                  <IconButton
                    component={Link}
                    to={`/${currentLang}/login`}
                    color="primary"
                    size="medium"
                    sx={{
                      mr: 1,
                      bgcolor: "rgba(25, 118, 210, 0.1)",
                      "&:hover": {
                        bgcolor: "rgba(25, 118, 210, 0.2)",
                      },
                    }}
                  >
                    <LogIn size={20} />
                  </IconButton>
                )}

                <IconButton
                  aria-label="show more"
                  aria-haspopup="true"
                  onClick={handleMobileDrawerOpen}
                  color="inherit"
                  edge="end"
                  size="medium"
                  sx={{ ml: 1 }}
                >
                  <MenuIcon size={20} />
                </IconButton>
              </Box>

              {/* User profile */}
              {user && (
                <Tooltip title={t("account")}>
                  <IconButton
                    onClick={handleProfileMenuOpen}
                    size="small"
                    sx={{ ml: 1 }}
                    aria-controls={profileOpen ? "account-menu" : undefined}
                    aria-haspopup="true"
                    aria-expanded={profileOpen ? "true" : undefined}
                  >
                    <Avatar
                      src={avatarSrc}
                      alt={displayName || displayEmail || t("account")}
                      sx={{ width: 32, height: 32, bgcolor: "#1976D2" }}
                    >
                      {avatarLetter}
                    </Avatar>
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Profile Menu */}
      <Menu
        anchorEl={profileAnchorEl}
        id="account-menu"
        open={profileOpen}
        onClose={handleProfileMenuClose}
        onClick={handleProfileMenuClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: "visible",
            filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.1))",
            mt: 1.5,
            width: 220,
            "& .MuiAvatar-root": {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        {user && (
          <>
            <Box sx={{ px: 2, py: 1 }}>
              <Typography variant="subtitle2" fontWeight={600}>
                {displayName || t("your_name")}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {displayEmail}
              </Typography>
            </Box>
            <Divider />
            <MenuItem
              sx={{ py: 1.5 }}
              onClick={() => openLink(`/${currentLang}/profile`)}
            >
              <ListItemIcon>
                <User size={18} />
              </ListItemIcon>
              {t("profile")}
            </MenuItem>
            <MenuItem
              sx={{ py: 1.5 }}
              onClick={() => openLink(`/${currentLang}/change-password`)}
            >
              <ListItemIcon>
                <Lock size={18} />
              </ListItemIcon>
              {t("change_password")}
            </MenuItem>
            <Divider />
            <MenuItem sx={{ py: 1.5, color: "error.main" }} onClick={logout}>
              <ListItemIcon>
                <LogOut size={18} color={theme.palette.error.main} />
              </ListItemIcon>
              {t("logout")}
            </MenuItem>
          </>
        )}
      </Menu>

      {/* Language Menu */}
      <Menu
        id="language-menu"
        anchorEl={languageAnchorEl}
        keepMounted
        open={languageOpen}
        onClose={handleLanguageMenuClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: "visible",
            filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.1))",
            mt: 1.5,
            width: 360,
            maxWidth: "calc(100vw - 32px)",
            p: 1,
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            gap: 0.5,
          }}
        >
          {Object.keys(lngs).map((lng) => (
            <MenuItem
              key={lng}
              onClick={(e) => handleLanguageChange(lng, e)}
              sx={{
                py: 1.25,
                px: 1.5,
                minHeight: 42,
                borderRadius: 1,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                whiteSpace: "normal",
              }}
              selected={currentLang === lng}
            >
              <Typography sx={{ width: "100%", fontSize: "0.9rem" }}>
                {lngs[lng].nativeName}
              </Typography>
              {currentLang === lng && (
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: "primary.main",
                    ml: 1,
                    flexShrink: 0,
                  }}
                />
              )}
            </MenuItem>
          ))}
        </Box>
      </Menu>

      {/* Mobile drawer */}
      {renderMobileDrawer}

      {/* Cart drawer */}
      <CartDrawer />
    </Box>
  );
}
